import React, { useState, useEffect } from 'react';
import { signUp, confirmSignUp, signIn } from 'aws-amplify/auth';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { APP_VERSION } from '../../version';
import { accessControlService } from '../../services/accessControlService';
import { UserPlus, AlertTriangle } from 'lucide-react';

interface InviteInfo {
  invite_id: string;
  org_id: string;
  org_name: string;
  invitee_email: string;
  role: string;
}

export const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'signup' | 'verify'>('signup');
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for invite parameter in URL
  useEffect(() => {
    const inviteId = searchParams.get('invite');
    if (inviteId) {
      loadInviteDetails(inviteId);
    }
  }, [searchParams]);

  const loadInviteDetails = async (inviteId: string) => {
    setIsLoadingInvite(true);
    setInviteError(null);

    try {
      const result = await accessControlService.lookupInvite(inviteId);

      if (result.success && result.org_name) {
        setInviteInfo({
          invite_id: result.invite_id!,
          org_id: result.org_id!,
          org_name: result.org_name,
          invitee_email: result.invitee_email || '',
          role: result.role || 'member',
        });
        // Pre-fill email if provided
        if (result.invitee_email) {
          setEmail(result.invitee_email);
        }
      } else {
        setInviteError(result.error || 'Invalid or expired invite');
      }
    } catch (error) {
      console.error('Error loading invite:', error);
      setInviteError('Failed to load invite details');
    } finally {
      setIsLoadingInvite(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }

    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      
      toast.success('Verification code sent to your email!');
      setStep('verify');
    } catch (error: any) {
      // console.error('SignUp error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: verificationCode,
      });

      toast.success('Account verified successfully!');

      // If there's an invite, auto sign-in and accept it
      if (inviteInfo) {
        // Validate email matches invite (if specified)
        if (inviteInfo.invitee_email && email.toLowerCase() !== inviteInfo.invitee_email.toLowerCase()) {
          toast.error(`This invite was sent to ${inviteInfo.invitee_email}. Please use that email.`);
          navigate('/login');
          return;
        }

        try {
          // Sign in the new user
          await signIn({
            username: email,
            password,
          });

          // Accept the invite
          try {
            const acceptResult = await accessControlService.acceptInvite(
              inviteInfo.org_id,
              inviteInfo.invite_id
            );

            if (acceptResult.success) {
              toast.success(`Welcome to ${inviteInfo.org_name}!`);
              navigate('/dashboard');
            } else {
              toast.error(`Invite acceptance failed: ${acceptResult.error}. Please try accepting from the dashboard.`);
              navigate('/dashboard');
            }
          } catch (acceptError) {
            console.error('Invite acceptance error:', acceptError);
            toast.error('Failed to accept invite. Please try again from the dashboard.');
            navigate('/dashboard');
          }
        } catch (signInError: any) {
          console.error('Auto sign-in failed:', signInError);
          toast.info('Please sign in to complete your invite acceptance.');
          navigate(`/login?invite=${inviteInfo.invite_id}`);
        }
      } else {
        toast.success('Please sign in with your new account.');
        navigate('/login');
      }
    } catch (error: any) {
      toast.error(error.message || 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      toast.success('Verification code resent!');
    } catch (error: any) {
      toast.error('Failed to resend code. Please try again.');
    }
  };

  if (step === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">Verify Your Email</CardTitle>
            <CardDescription className="text-center">
              We sent a verification code to {email}
            </CardDescription>
          </CardHeader>
          <form onSubmit={handleVerification}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="verification-code">Verification Code</Label>
                <Input
                  id="verification-code"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  disabled={isLoading}
                  maxLength={6}
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 pt-6 pb-6">
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify Account'}
              </Button>
              <div className="text-sm text-center space-y-2">
                <button
                  type="button"
                  onClick={resendCode}
                  className="text-primary hover:underline"
                >
                  Resend verification code
                </button>
                <div>
                  <Link
                    to="/login"
                    className="text-primary hover:underline"
                  >
                    Back to sign in
                  </Link>
                </div>
              </div>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-baseline justify-center gap-2">
            <CardTitle className="text-3xl font-bold text-center font-mono">BigSplice</CardTitle>
            <span className="text-sm text-muted-foreground font-mono">v{APP_VERSION}</span>
          </div>
          <CardDescription className="text-center">
            {inviteInfo ? 'Create your account to accept your invitation' : 'Create your account to get started'}
          </CardDescription>

          {/* Invite Banner */}
          {isLoadingInvite && (
            <div className="text-center text-sm text-muted-foreground py-2">
              Loading invitation details...
            </div>
          )}

          {inviteInfo && (
            <Alert className="mt-4 border-primary/50 bg-primary/5">
              <UserPlus className="h-4 w-4 text-primary" />
              <AlertDescription className="ml-2">
                <span className="font-medium">You've been invited to join </span>
                <span className="font-bold text-primary">{inviteInfo.org_name}</span>
                <span className="font-medium"> as a </span>
                <span className="font-semibold">{inviteInfo.role}</span>
              </AlertDescription>
            </Alert>
          )}

          {inviteError && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                {inviteError}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <form onSubmit={handleSignUp}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                readOnly={!!inviteInfo?.invitee_email}
                className={inviteInfo?.invitee_email ? 'bg-muted' : ''}
              />
              {inviteInfo?.invitee_email && (
                <p className="text-xs text-muted-foreground">
                  This email was specified in your invitation
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                minLength={8}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4 pt-6 pb-6">
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </Button>
            <div className="text-sm text-center">
              Already have an account?{' '}
              <Link
                to={inviteInfo ? `/login?invite=${inviteInfo.invite_id}` : '/login'}
                className="text-primary hover:underline"
              >
                Sign in
              </Link>
            </div>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}; 